import { Box, CSSObject, Typography, TypographyProps, styled } from '@mui/material';
import { Fragment, ReactElement } from 'react';

import { CopyIconButton } from '../../feedback/CopyIconButton';

type WrappingBoxProps = {
  disableMargins: boolean;
  attachBottom: boolean;
  extra?: CSSObject;
};

const WrappingBox = styled(Box, {
  shouldForwardProp: (prop) => !['disableMargins', 'attachBottom', 'extra'].includes(prop as string),
})<WrappingBoxProps>(({ theme, disableMargins, attachBottom, extra }) => ({
  borderRadius: 5,
  border: '1px solid',
  background: theme.palette.background.default,
  borderColor: theme.palette.secondary.dark,
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  padding: theme.spacing(1),

  ...(attachBottom && {
    borderBottom: 'none',
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
  }),

  marginTop: disableMargins ? 0 : theme.spacing(2),
  marginBottom: disableMargins ? 0 : theme.spacing(2),
  ...extra,
}));

const IconsBox = styled(Box)(({ theme }) => ({
  paddingLeft: theme.spacing(2),
  gap: theme.spacing(0.5),
  display: 'flex',
  alignItems: 'center',
  alignSelf: 'center',
}));

export type CodeVisualizerProps = {
  /** Lines of code or commands to render inside the visualizer. */
  commands: string[];
  /** Extra props to pass to the shown text. */
  typographyProps?: TypographyProps;
  /** Extra style to pass to the container box. */
  boxStyling?: CSSObject;

  /** Whether to show a copy-to-clipboard button. */
  showCopy?: boolean;

  /** Removes vertical margins around the component. */
  disableMargins?: boolean;

  /** Renders all code in a single line with ellipsis truncation. */
  singleLineMode?: boolean;

  /**
   * If true, removes bottom border and radius to visually attach another component below.
   */
  attachBottom?: boolean;
};

export const CodeVisualizer = ({
  commands,
  typographyProps,
  boxStyling,
  showCopy = true,
  disableMargins = false,
  singleLineMode = false,
  attachBottom = false,
}: CodeVisualizerProps): ReactElement => {
  return (
    <WrappingBox disableMargins={disableMargins} attachBottom={attachBottom} extra={boxStyling}>
      {/* minWidth={0} is needed to avoid the overflow the flex item and respect the parent width */}
      <Box minWidth={0} mt={0.4}>
        {commands.map((log) => (
          <Fragment key={log}>
            <Typography
              fontFamily="monospace"
              fontSize={12}
              fontWeight={600}
              color="text.tertiary"
              sx={{
                textOverflow: singleLineMode ? 'ellipsis' : undefined,
                whiteSpace: singleLineMode ? 'nowrap' : undefined,
                overflow: 'hidden',
              }}
              title={log}
              {...typographyProps}
            >
              {log}
              <br />
            </Typography>
          </Fragment>
        ))}
      </Box>
      <IconsBox>
        {showCopy && (
          <CopyIconButton
            textToCopy={commands.join('\n')}
            tooltipTitle="Copy to clipboard"
            iconFontSize="small"
            sx={(theme) => ({ color: theme.palette.navigation.contrastText, padding: 0 })}
          />
        )}
      </IconsBox>
    </WrappingBox>
  );
};
